import React, { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';
import type { SiteRagReport } from '../../../types/siteRag';
import { CONFIDENCE_BANDS } from '../../../lib/audit/confidenceTaxonomy';
import { applyCaveatRewrites, applyCaveatRewritesToReport } from '../../../lib/audit/caveatHonestyVerifier';
import type { CaveatHonestyFinding } from '../../../lib/audit/caveatHonestyVerifier';
import { Button } from '../../ui/button';
import { useStore } from '../../../store';

type CaveatFinding = CaveatHonestyFinding;

export function CaveatHonestyPanel({ reports }: { reports: SiteRagReport[] }) {
  const updateRagReport = useStore((s) => s.updateRagReport);
  const [previewByReport, setPreviewByReport] = useState<Record<string, string>>({});

  const rows = useMemo(
    () => reports.flatMap((report) =>
      (report.qaFindings?.caveatHonesty?.findings ?? []).map((f) => ({ report, f: f as CaveatFinding })),
    ),
    [reports],
  );

  const handleApplyRewrite = useCallback(async (report: SiteRagReport, finding: CaveatFinding) => {
    if (!finding.suggestedRewrite) return;
    // The report body is NOT `report.markdown` (a legacy-only field, undefined
    // on structured reports) — it lives across structured prose fields
    // (executiveSummary / summary / conclusion / EIA sections / findings).
    // `applyCaveatRewritesToReport` walks those fields, applies the same
    // substring softening `applyCaveatRewrites` does, and returns a rewritten
    // report we persist via `updateRagReport` (the supported client-side body
    // write — full structured body to GCS + Firestore metadata). When the
    // excerpt isn't found verbatim in any field, `applied === 0` and we surface
    // that instead of silently no-op'ing.
    const { report: rewritten, applied } = applyCaveatRewritesToReport(report, [finding], 'autocorrect');
    if (applied === 0) {
      toast.error('Could not locate the flagged text in the report body to rewrite.');
      return;
    }
    try {
      await updateRagReport(report.id, rewritten as Partial<SiteRagReport>);
      toast.success('Applied caveat rewrite to the report.');
      setPreviewByReport((prev) => {
        const next = { ...prev };
        delete next[`${report.id}:${finding.claimId}`];
        return next;
      });
    } catch (err) {
      console.error('[caveat-honesty] apply rewrite failed', err);
      toast.error('Failed to save the caveat rewrite. Please try again.');
    }
  }, [updateRagReport]);

  const handlePreview = useCallback((report: SiteRagReport, finding: CaveatFinding) => {
    const md = (report as SiteRagReport & { markdown?: string }).markdown ?? finding.excerpt;
    const { markdown } = applyCaveatRewrites(md, [finding], 'preview');
    setPreviewByReport((prev) => ({ ...prev, [`${report.id}:${finding.claimId}`]: markdown }));
  }, []);

  if (rows.length === 0) return null;

  return (
    <section className="caveat-honesty-panel mt-4" data-testid="caveat-honesty-panel">
      <h3 className="text-sm font-bold uppercase tracking-widest text-text-secondary mb-2">
        Caveat Honesty (R-75)
      </h3>
      <p className="text-sm text-text-secondary mb-3">
        Per-claim declared vs actual confidence bands. Auto-rewrite is gated by{' '}
        <code>VITE_AUDIT_AUTOCORRECT_CAVEATS</code> and never runs on block findings.
      </p>
      <ul className="space-y-3 text-sm">
        {rows.slice(0, 25).map(({ report, f }) => {
          const previewKey = `${report.id}:${f.claimId}`;
          const declaredLabel = CONFIDENCE_BANDS[f.declaredBand as keyof typeof CONFIDENCE_BANDS]?.label ?? f.declaredBand;
          const actualLabel = CONFIDENCE_BANDS[f.actualBand as keyof typeof CONFIDENCE_BANDS]?.label ?? f.actualBand;
          return (
            <li
              key={previewKey}
              className={`rounded border px-3 py-2 ${
                f.severity === 'block'
                  ? 'border-danger/40 bg-danger/10 text-text-primary'
                  : 'border-warning/40 bg-warning/10 text-text-primary'
              }`}
              data-testid={`caveat-honesty-finding-${f.claimId}`}
            >
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <span className="font-medium">{report.reportType ?? report.id}</span>
                <span className="text-xs uppercase">{f.severity}</span>
                <span className="text-xs">
                  declared: {declaredLabel} → actual: {actualLabel}
                </span>
              </div>
              <p className="text-xs italic mb-2">&ldquo;{f.excerpt}&rdquo;</p>
              <dl className="text-xs grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-1 mb-2">
                <div><dt className="text-text-secondary">Tier-1</dt><dd>{f.evidenceBreakdown.tier1Score.toFixed(2)}</dd></div>
                <div><dt className="text-text-secondary">Tier-2</dt><dd>{f.evidenceBreakdown.tier2Score.toFixed(2)}</dd></div>
                <div><dt className="text-text-secondary">Tier-3</dt><dd>{f.evidenceBreakdown.tier3Score.toFixed(2)}</dd></div>
                <div><dt className="text-text-secondary">Multi-source</dt><dd>+{f.evidenceBreakdown.multiSourceBonus.toFixed(2)}</dd></div>
                <div><dt className="text-text-secondary">Broken cite</dt><dd>{f.evidenceBreakdown.brokenCitePenalty.toFixed(2)}</dd></div>
                <div><dt className="text-text-secondary">Provenance gap</dt><dd>{f.evidenceBreakdown.provenanceGapPenalty.toFixed(2)}</dd></div>
              </dl>
              {f.severity === 'warn' && f.suggestedRewrite && (
                <div className="flex gap-2 mt-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => handlePreview(report, f)}
                    data-testid={`caveat-preview-${f.claimId}`}
                  >
                    Preview rewrite
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => { void handleApplyRewrite(report, f); }}
                    data-testid={`caveat-apply-${f.claimId}`}
                  >
                    Apply rewrite
                  </Button>
                </div>
              )}
              {previewByReport[previewKey] && (
                <p className="text-xs mt-2 p-2 bg-surface-1/60 rounded border border-border/50">
                  {previewByReport[previewKey]}
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
